import type { ReactNode } from 'react'
import { Breadcrumbs } from '@/ui/Breadcrumbs/Breadcrumbs'
import { Band } from '@/ui/Console/Console'
import { Container } from '@/ui/layout/Container'
import { Inline } from '@/ui/layout/Inline'
import { Text } from '@/ui/layout/Text'
import { useDocumentTitle } from './useDocumentTitle'
import styles from './WsPageShell.module.css'

export interface Crumb {
  label: string
  to?: string
}

/**
 * Shell for the full-width workspace pages (Resources, Skills, Memory, Runs,
 * agent profiles): the breadcrumb band, then a scrolling column holding the
 * page's one `<h1>`, its description and its body. Renders to the right of the
 * rail. The band shows only when something sits above the page (two or more
 * crumbs); a root-level page passes none and its title says where you are. The
 * document title is the page title, or the last crumb when the title is not
 * plain text.
 */
export function WsPageShell({
  crumbs,
  title,
  meta,
  lead,
  description,
  actions,
  width = 'container-content',
  titleId,
  children,
}: {
  crumbs: Crumb[]
  title?: ReactNode
  meta?: ReactNode
  /** A mark before the title (a `lg` Avatar), aligned to the title's line. */
  lead?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  width?: 'container-content' | 'container-table'
  /** The h1's id, for a control the page title names (aria-labelledby). */
  titleId?: string
  children: ReactNode
}) {
  useDocumentTitle(
    typeof title === 'string' && title ? title : (crumbs.at(-1)?.label ?? ''),
  )
  const heading = (
    <Text as="h1" variant="title" id={titleId}>
      {title}
    </Text>
  )
  return (
    <div className={styles.shell}>
      {crumbs.length > 1 && (
        <Band>
          <Breadcrumbs
            crumbs={crumbs.map((c) => ({ label: c.label, href: c.to }))}
          />
        </Band>
      )}
      <div className={styles.scroll}>
        <Container size={width} inset>
          {(title || actions) && (
            <div className={styles.header}>
              {title && (
                <div className={styles.title}>
                  {lead ? (
                    <div className={styles.titleRow}>
                      {lead}
                      {heading}
                    </div>
                  ) : (
                    heading
                  )}
                  {meta != null && (
                    <Text
                      as="p"
                      variant="caption"
                      tone="text-mute"
                      numeric
                      className={styles.meta}
                    >
                      {meta}
                    </Text>
                  )}
                </div>
              )}
              {actions && (
                <Inline gap="space-8" className={styles.actions}>
                  {actions}
                </Inline>
              )}
            </div>
          )}
          {description && (
            <Text as="p" tone="text-mute" className={styles.description}>
              {description}
            </Text>
          )}
          <div className={styles.body}>{children}</div>
        </Container>
      </div>
    </div>
  )
}
